// The Summit Run soundtrack.
//
// Generative rather than a file: no asset to download, works offline, never
// loops audibly, and the whole thing is under a hundred lines. Alto's score is
// slow ambient pads with a sparse melody over the top, and that shape happens
// to be the easiest kind of music to generate honestly.
//
// Every note is drawn from a pentatonic scale, which is the trick that makes
// this work. There is no wrong note in a pentatonic scale, so notes can be
// picked at random and the result is always consonant. Each mountain gets its
// own root, so the ten runs sound different without ten compositions.
//
// It obeys the same mute switch as the rest of the app, and it only ever starts
// from a user gesture, which the browser insists on anyway.
import { soundEnabled } from "./effects";

// Minor pentatonic, in semitones. Chosen over major for the same reason the
// palettes walk into dusk: it sounds like weather and altitude rather than
// like a birthday.
const SCALE = [0, 3, 5, 7, 10, 12, 15, 17];
const ROOTS = [196.0, 174.6, 220.0, 164.8, 146.8, 130.8, 174.6, 155.6, 130.8, 185.0];

interface Live {
  ctx: AudioContext;
  master: GainNode;
  pad: OscillatorNode[];
  timer: number | null;
  stopped: boolean;
}

let live: Live | null = null;

function note(semitones: number, root: number): number {
  return root * Math.pow(2, semitones / 12);
}

export function startRunMusic(mountainIndex: number): void {
  stopRunMusic();
  if (!soundEnabled()) return;
  let ctx: AudioContext;
  try {
    ctx = new AudioContext();
  } catch {
    return;
  }
  if (ctx.state === "suspended") void ctx.resume();

  const root = ROOTS[mountainIndex % ROOTS.length] ?? 196;
  const master = ctx.createGain();
  // Quiet. This plays under a game a child is concentrating on, and the point
  // of it is that they would notice its absence rather than its presence.
  master.gain.value = 0;
  master.gain.linearRampToValueAtTime(0.09, ctx.currentTime + 3);
  master.connect(ctx.destination);

  // A low-pass filter over everything takes the edge off the oscillators, which
  // is most of the difference between "ambient pad" and "test tone".
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 1400;
  filter.Q.value = 0.6;
  filter.connect(master);

  // The pad: root, fifth and octave held forever, each detuned a few cents from
  // the next so they beat slowly against each other and the chord breathes.
  const pad: OscillatorNode[] = [];
  for (const [semis, detune, gain] of [[0, -4, 0.5], [7, 3, 0.34], [12, -7, 0.2]] as const) {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = note(semis, root) / 2;
    osc.detune.value = detune;
    const g = ctx.createGain();
    g.gain.value = gain;
    osc.connect(g); g.connect(filter);
    osc.start();
    pad.push(osc);
  }

  const state: Live = { ctx, master, pad, timer: null, stopped: false };
  live = state;

  // The melody. One note every second and a half or so, from the scale, with
  // rests, so it never becomes a tune anyone has to listen to.
  const pluck = () => {
    if (state.stopped || !live) return;
    if (Math.random() < 0.32) {
      const step = SCALE[Math.floor(Math.random() * SCALE.length)] ?? 0;
      const osc = ctx.createOscillator();
      osc.type = "triangle";
      osc.frequency.value = note(step, root) * 2;
      const g = ctx.createGain();
      const now = ctx.currentTime;
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.16, now + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 2.4);
      osc.connect(g); g.connect(filter);
      osc.start(now);
      osc.stop(now + 2.5);
    }
    state.timer = window.setTimeout(pluck, 700 + Math.random() * 900);
  };
  state.timer = window.setTimeout(pluck, 1200);
}

export function stopRunMusic(): void {
  const state = live;
  live = null;
  if (!state) return;
  state.stopped = true;
  if (state.timer !== null) window.clearTimeout(state.timer);
  const end = state.ctx.currentTime + 0.7;
  try {
    // Fade rather than cut, then close the context so it stops costing
    // anything. A hard stop on a sine wave is an audible click.
    state.master.gain.cancelScheduledValues(state.ctx.currentTime);
    state.master.gain.setValueAtTime(state.master.gain.value, state.ctx.currentTime);
    state.master.gain.linearRampToValueAtTime(0, end);
    for (const osc of state.pad) osc.stop(end + 0.05);
  } catch {
    // An already-closed context throws; nothing to clean up if so.
  }
  window.setTimeout(() => { void state.ctx.close().catch(() => {}); }, 1100);
}
